import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { ArrowRight } from "lucide-react";

const EntryPage = () => {
  const { t, lang, setLang } = useLanguage();

  return (
    <div data-testid="entry-page" className="min-h-screen w-full flex flex-col bg-[#FDFDFD] text-[#111] relative overflow-hidden">
      {/* minimal header */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-center px-6 md:px-12 lg:px-20 py-6">
        <Link to="/" data-testid="entry-back-stage" className="absolute left-6 md:left-12 lg:left-20 top-1/2 -translate-y-1/2 hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-neutral-500 hover:text-black transition">
          <span>←</span> Zurück
        </Link>
        <img
          data-testid="entry-brand"
          src="/assets/green-horizon-logo-transparent.png"
          alt="Green Horizon Life — Enrico Müller & Colleen Schmidt"
          className="h-24 md:h-28 lg:h-32 w-auto"
        />
        <div data-testid="entry-lang-switcher" className="absolute right-6 md:right-12 lg:right-20 top-1/2 -translate-y-1/2 flex items-center text-[0.7rem] uppercase tracking-[0.24em] border border-neutral-300 rounded-full overflow-hidden bg-white/70 backdrop-blur">
          <button data-testid="entry-lang-de" onClick={() => setLang("de")} className={`px-3 py-1.5 ${lang === "de" ? "text-black font-semibold" : "text-neutral-400 hover:text-black"} transition`}>DE</button>
          <span className="w-px h-3 bg-neutral-300" />
          <button data-testid="entry-lang-en" onClick={() => setLang("en")} className={`px-3 py-1.5 ${lang === "en" ? "text-black font-semibold" : "text-neutral-400 hover:text-black"} transition`}>EN</button>
        </div>
      </div>

      {/* The central question */}
      <div className="w-full flex flex-col items-center justify-center text-center px-6 pt-40 md:pt-48 lg:pt-52 pb-10 max-w-4xl mx-auto fade-up">
        <span className="eyebrow text-neutral-500">{t.entry.eyebrow}</span>
        <h1 className="entry-display mt-6 text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-[#111]">
          {t.entry.question}
        </h1>
        <p className="mt-5 text-base md:text-lg text-neutral-500 max-w-2xl">
          {t.entry.lead}
        </p>
      </div>

      {/* Split-screen */}
      <div className="relative flex-1 flex flex-col md:flex-row min-h-[58vh] md:min-h-[62vh] border-t border-neutral-200">
        {/* LEFT — Women */}
        <Link
          to="/women"
          data-testid="entry-women-button"
          className="split-side relative flex flex-col justify-between p-10 md:p-16 lg:p-24 group bg-[#F4F1ED] hover:bg-[#EDE7DF] transition-colors overflow-hidden"
        >
          {/* atmospheric background — Bali / spirituality */}
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.32] group-hover:opacity-[0.42] transition-opacity duration-700"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1600&q=80')",
              filter: "saturate(0.8) sepia(0.12)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F4F1ED]/65 via-[#F4F1ED]/35 to-[#F4F1ED]/20" />
          <div className="relative z-10">
            <span className="eyebrow text-[#5C6660]">{t.entry.women.eyebrow}</span>
            <h2 className="mt-6 font-[Cormorant_Garamond] text-4xl md:text-5xl lg:text-6xl leading-tight text-[#2C332E]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}>
              {t.entry.women.title}<br />
              <span className="italic text-[#738678]">{t.entry.women.subtitle}</span>
            </h2>
          </div>
          <div className="relative z-10 mt-12 flex items-center justify-between">
            <span className="inline-flex items-center gap-3 text-[#2C332E] text-sm uppercase tracking-[0.24em]">
              {t.entry.women.action}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-2" />
            </span>
            <span className="text-5xl md:text-7xl text-[#D9C5B2]/80 leading-none" style={{ fontFamily: 'Cormorant Garamond, serif' }}>I</span>
          </div>
          <div className="grain" />
        </Link>

        {/* divider */}
        <div className="entry-divider" />

        {/* RIGHT — Men */}
        <Link
          to="/men"
          data-testid="entry-men-button"
          className="split-side relative flex flex-col justify-between p-10 md:p-16 lg:p-24 group bg-[#0F1014] hover:bg-[#15171D] transition-colors text-white overflow-hidden"
        >
          {/* atmospheric background — mountain peak / dramatic ascent */}
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.35] group-hover:opacity-[0.45] transition-opacity duration-700"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1600&q=80')",
              filter: "saturate(0.45) contrast(1.15) brightness(0.85)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-[#0F1014]/70 via-[#0F1014]/40 to-[#0F1014]/20" />
          <div className="relative z-10">
            <span className="eyebrow text-[#8A91A5]">{t.entry.men.eyebrow}</span>
            <h2 className="mt-6 text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 600 }}>
              {t.entry.men.title}<br />
              <span className="text-[#D4AF37]">{t.entry.men.subtitle}</span>
            </h2>
          </div>
          <div className="relative z-10 mt-12 flex items-center justify-between">
            <span className="inline-flex items-center gap-3 text-white text-[0.78rem] uppercase tracking-[0.28em]">
              {t.entry.men.action}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-2" />
            </span>
            <span className="text-5xl md:text-7xl text-[#D4AF37]/70 leading-none" style={{ fontFamily: 'Clash Display, Cabinet Grotesk, sans-serif', fontWeight: 700 }}>II</span>
          </div>
          <div className="grain" />
        </Link>
      </div>

      <div className="text-center text-[0.72rem] uppercase tracking-[0.28em] text-neutral-400 py-6">
        {t.entry.footer}
      </div>
    </div>
  );
};

export default EntryPage;
